'use client';

import { useState } from 'react';
import { ChevronDown } from '@untitledui/icons';
import { Input } from '@untitledui/react/components/base/input/input';
import { RadioButton, RadioGroup } from '@untitledui/react/components/base/radio-buttons/radio-buttons';
import { TextArea } from '@untitledui/react/components/base/textarea/textarea';
import {
  ETATS,
  FIELDS,
  RECOMMENDED_FIELD_HINT,
  TYPE_BY_KEY,
  USAGES,
  fieldHelp,
  type Etat,
  type EquipmentValues,
  type FieldKey,
  type TypeKey,
  type Usage,
} from '@caverne/shared';

import { HelpDisclosure } from './help-disclosure';
import { ScreenSizePicker } from './screen-size-picker';

export interface EquipmentDraft {
  typeKey: TypeKey;
  vals: EquipmentValues;
  etat: Etat;
  usage: Usage;
}

interface EquipmentFormProps {
  draft: EquipmentDraft;
  onChange: (draft: EquipmentDraft) => void;
  /** Erreurs bloquantes (format du n° d'inventaire). */
  errors: Partial<Record<FieldKey, string>>;
  /** Après une tentative de validation, marque les champs vides « recommandés ». */
  showRecommended?: boolean;
  /** Lieu déduit de la situation — affiché, jamais saisi. */
  locationLabel: string;
  /** Visuel du sélecteur de taille d'écran, résolu par l'application. */
  screenSizeImageSrc: string;
  /**
   * Dossier public des illustrations d'aide (`publicAsset('/assets/help')`). Absent, les
   * aides gardent leur cadre « Illustration à venir ».
   */
  helpMediaBase?: string;
}

/**
 * Formulaire d'un matériel : les champs de son type, puis un volet « Précisions »
 * (lieu, état, utilisation) pré-rempli. Piloté par le catalogue de `@caverne/shared`,
 * donc utilisable partout où l'on saisit un matériel : assistant, modification, demande.
 *
 * Aucun champ n'est requis — la saisie partielle est autorisée. Seul le format du n°
 * d'inventaire bloque ; les champs vides reçoivent la mention « Champ recommandé ».
 */
export function EquipmentForm({
  draft,
  onChange,
  errors,
  showRecommended = false,
  locationLabel,
  screenSizeImageSrc,
  helpMediaBase,
}: EquipmentFormProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const fields = TYPE_BY_KEY[draft.typeKey].fields;

  const setValue = (fieldKey: FieldKey, value: string) =>
    onChange({ ...draft, vals: { ...draft.vals, [fieldKey]: value } });

  return (
    <div className="flex flex-col gap-5">
      {fields.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.map((fieldKey) => {
            const field = FIELDS[fieldKey];
            const value = draft.vals[fieldKey] ?? '';
            const error = errors[fieldKey];
            const hint = error ?? (showRecommended && !value ? RECOMMENDED_FIELD_HINT : undefined);
            // L'aide se décline par type : trouver le nom d'un poste, d'une tablette ou d'un
            // téléphone ne passe pas par le même chemin.
            const help = fieldHelp(draft.typeKey, fieldKey);

            return (
              <div key={fieldKey} className={field.wide ? 'sm:col-span-2' : undefined}>
                {field.kind === 'text' && (
                  <Input
                    label={field.label}
                    placeholder={field.placeholder}
                    value={value}
                    onChange={(next) => setValue(fieldKey, next)}
                    isInvalid={Boolean(error)}
                    hint={hint}
                  />
                )}

                {field.kind === 'area' && (
                  <TextArea
                    label={field.label}
                    placeholder={field.placeholder}
                    value={value}
                    onChange={(next) => setValue(fieldKey, next)}
                    hint={hint}
                    rows={3}
                  />
                )}

                {field.kind === 'radio' && (
                  <RadioGroup
                    value={value}
                    onChange={(next) => setValue(fieldKey, next)}
                    aria-label={field.label}
                  >
                    <span className="text-secondary text-sm font-medium">{field.label}</span>
                    <div className="flex flex-wrap gap-4">
                      {(field.options ?? []).map((option) => (
                        <RadioButton key={option} value={option} label={option} />
                      ))}
                    </div>
                  </RadioGroup>
                )}

                {field.kind === 'size' && (
                  <ScreenSizePicker
                    value={value}
                    onChange={(next) => setValue(fieldKey, next)}
                    imageSrc={screenSizeImageSrc}
                  />
                )}

                {help && <HelpDisclosure help={help} mediaBase={helpMediaBase} />}
              </div>
            );
          })}
        </div>
      )}

      <div className="border-secondary rounded-lg border">
        <button
          type="button"
          onClick={() => setIsDetailsOpen((open) => !open)}
          aria-expanded={isDetailsOpen}
          className="text-secondary flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
        >
          Précisions
          <ChevronDown className={`size-4 transition-transform ${isDetailsOpen ? 'rotate-180' : ''}`} />
        </button>

        {isDetailsOpen && (
          <div className="border-secondary flex flex-col gap-5 border-t px-4 py-4">
            <div>
              <p className="text-secondary text-sm font-medium">Lieu</p>
              <p className="text-tertiary mt-1 text-sm">{locationLabel}</p>
            </div>

            <RadioGroup
              value={draft.etat}
              onChange={(next) => onChange({ ...draft, etat: next as Etat })}
              aria-label="État"
            >
              <span className="text-secondary text-sm font-medium">État</span>
              <div className="flex flex-wrap gap-4">
                {Object.entries(ETATS).map(([key, label]) => (
                  <RadioButton key={key} value={key} label={label} />
                ))}
              </div>
            </RadioGroup>

            <RadioGroup
              value={draft.usage}
              onChange={(next) => onChange({ ...draft, usage: next as Usage })}
              aria-label="Utilisation"
            >
              <span className="text-secondary text-sm font-medium">Utilisation</span>
              <div className="flex flex-wrap gap-4">
                {Object.entries(USAGES).map(([key, label]) => (
                  <RadioButton key={key} value={key} label={label} />
                ))}
              </div>
            </RadioGroup>
          </div>
        )}
      </div>
    </div>
  );
}
